import { z, ARABIC_LATIN_NAME_REGEX } from '@tasama/utils';

export const COMPANY_TYPE_OPTIONS = [
  { value: 'llc', labelKey: 'companyDetails.companyType.options.llc' },
  { value: 'jsc', labelKey: 'companyDetails.companyType.options.jsc' },
  { value: 'sjsc', labelKey: 'companyDetails.companyType.options.sjsc' },
];

export const CURRENCY_OPTIONS = [
  { value: 'SAR', label: 'SAR', countryCode: 'sa' },
];

const nameRegex = ARABIC_LATIN_NAME_REGEX;
const numericRegex = /^[0-9]*\.?[0-9]*$/;

export const getCompanyDetailsFormSchema = (t: (_key: string) => string) => {
  const ns = 'companyDetails.validation';
  return z.object({
    companyType: z.string().min(1, t(`${ns}.companyType.required`)),

    sharedCapital: z
      .string()
      .min(1, t(`${ns}.sharedCapital.required`))
      .regex(numericRegex, t(`${ns}.sharedCapital.invalid`)),

    currency: z.string().min(1, t(`${ns}.currency.required`)),

    ownershipStructureDocument: z
      .custom<File>(
        (val) => val instanceof File,
        t(`${ns}.ownershipDocument.required`)
      )
      .refine((file) => file.size > 0, t(`${ns}.ownershipDocument.empty`))
      .refine(
        (file) => file.size <= 5 * 1024 * 1024,
        t('companyDetails.ownershipDocument.errors.invalidSize')
      )
      .refine(
        (file) =>
          ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'].includes(
            file.type
          ),
        t('companyDetails.ownershipDocument.errors.invalidType')
      ),

    beneficialOwnerFirstName: z
      .string()
      .min(1, t(`${ns}.beneficialOwner.firstName.required`))
      .max(255, t(`${ns}.beneficialOwner.firstName.max`))
      .regex(nameRegex, t(`${ns}.beneficialOwner.firstName.charset`)),

    beneficialOwnerLastName: z
      .string()
      .min(1, t(`${ns}.beneficialOwner.lastName.required`))
      .max(255, t(`${ns}.beneficialOwner.lastName.max`))
      .regex(nameRegex, t(`${ns}.beneficialOwner.lastName.charset`)),

    beneficialOwnerPassportCopy: z
      .custom<File>(
        (val) => val instanceof File,
        t(`${ns}.beneficialOwner.passportCopy.required`)
      )
      .refine(
        (file) => file.size > 0,
        t(`${ns}.beneficialOwner.passportCopy.empty`)
      )
      .refine(
        (file) => file.size <= 5 * 1024 * 1024,
        t('companyDetails.beneficialOwner.passportCopy.errors.invalidSize')
      )
      .refine(
        (file) =>
          ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'].includes(
            file.type
          ),
        t('companyDetails.beneficialOwner.passportCopy.errors.invalidType')
      ),
  });
};

export const companyDetailsFormSchema = getCompanyDetailsFormSchema(
  (key) => key
);

export type CompanyDetailsFormValues = z.infer<typeof companyDetailsFormSchema>;

export const companyDetailsFormDefaultValues: Omit<
  CompanyDetailsFormValues,
  'ownershipStructureDocument' | 'beneficialOwnerPassportCopy'
> = {
  companyType: '',
  sharedCapital: '',
  currency: 'SAR',
  beneficialOwnerFirstName: '',
  beneficialOwnerLastName: '',
};
